<div class="overview-dashboard">
  <!-- Stylish Navigation Bar -->
  <div class="overview-navbar app-card">
    <div class="card-body p-2 d-flex align-items-center justify-content-between flex-wrap gap-2">
      <nav class="nav-segment-control" aria-label="Dashboard Overview Navigation">
        @for (tab of tabs; track tab.id) {
          <button
            type="button"
            class="nav-segment-item"
            [class.active]="activeTab === tab.id"
            (click)="selectTab(tab.id)"
          >
            <i [class]="tab.icon"></i>
            <span>{{ tab.label }}</span>
          </button>
        }
      </nav>

      <!-- Right Header Actions (Refresh & Last Updated) -->
      @if (activeTab === 'overview') {
        <div class="d-flex align-items-center gap-2">
          @if (lastUpdated) {
            <span class="text-muted fs-8 d-none d-md-inline">
              <i class="bi bi-clock me-1"></i>Updated: {{ lastUpdated | date:'shortTime' }}
            </span>
          }
          <button 
            type="button" 
            class="btn btn-sm btn-light border d-inline-flex align-items-center" 
            (click)="loadOverview()" 
            [disabled]="loading"
            title="Refresh overview data">
            <i class="bi bi-arrow-clockwise me-1" [class.spin]="loading"></i>
            <span>Refresh</span>
          </button>
        </div>
      }
    </div>
  </div>

  <!-- Tab Content -->
  <div class="overview-tab-content">
    <!-- ========================================================================= -->
    <!-- OVERVIEW TAB -->
    <!-- ========================================================================= -->
    @if (activeTab === 'overview') {
      <div class="tab-pane-container" id="overview-details">
        
        <!-- PERIOD FILTER TOOLBAR -->
        <div class="overview-filter-bar app-card p-3 mb-3">
          <div class="d-flex align-items-center justify-content-between flex-wrap gap-3">
            <!-- Preset Pills -->
            <div class="d-flex align-items-center gap-1 flex-wrap">
              <span class="fs-8 fw-semibold text-uppercase text-muted me-2">
                <i class="bi bi-calendar3 me-1"></i>Period:
              </span>
              @for (p of presets; track p.id) {
                <button 
                  type="button" 
                  class="btn btn-xs preset-filter-pill"
                  [class.active]="selectedPreset === p.id"
                  (click)="setPreset(p.id)">
                  {{ p.label }}
                </button>
              }
            </div>

            <!-- Custom Date Inputs (when Custom is selected or toggled) -->
            @if (showCustomDates || selectedPreset === 'custom') {
              <div class="d-flex align-items-center gap-2 flex-wrap">
                <div class="d-flex align-items-center gap-1">
                  <span class="fs-8 text-muted">From:</span>
                  <input type="date" class="form-control form-control-sm date-input" [(ngModel)]="startDate">
                </div>
                <div class="d-flex align-items-center gap-1">
                  <span class="fs-8 text-muted">To:</span>
                  <input type="date" class="form-control form-control-sm date-input" [(ngModel)]="endDate">
                </div>
                <button type="button" class="btn btn-sm btn-primary d-inline-flex align-items-center" (click)="applyCustomRange()">
                  <i class="bi bi-funnel me-1"></i> Apply
                </button>
              </div>
            } @else {
              <!-- Current Period Display Badge -->
              <div class="d-flex align-items-center gap-2">
                @if (overviewData?.period) {
                  <span class="badge bg-light text-dark border fs-8">
                    <i class="bi bi-calendar-check text-primary me-1"></i>
                    {{ overviewData?.period?.start_date | date:'dd MMM yyyy' }} - {{ overviewData?.period?.end_date | date:'dd MMM yyyy' }}
                  </span>
                }
              </div>
            }
          </div>
        </div>

        <!-- LOADING SPINNER -->
        @if (loading && !overviewData) {
          <div class="text-center py-5">
            <div class="spinner-border text-primary mb-3" style="width: 3rem; height: 3rem;"></div>
            <h6 class="fw-semibold text-dark">Loading Plant Overview Data...</h6>
            <p class="text-muted fs-7">Aggregating production, dispatches, and ledger metrics...</p>
          </div>
        }

        @if (overviewData) {
          <!-- ========================================================================= -->
          <!-- 1) FIRST LINE: CUSTOMER LIFECYCLE & CLOSURE PIPELINE -->
          <!-- ========================================================================= -->
          <div class="row g-3 mb-3">
            <!-- 1. Total Customers -->
            <div class="col-md-4">
              <div class="metric-card app-card p-3 h-100 metric-card-blue cursor-pointer" (click)="selectTab('customer_accounts')">
                <div class="d-flex align-items-start justify-content-between mb-2">
                  <div>
                    <span class="metric-label text-muted fs-8 fw-semibold text-uppercase">Total Customers</span>
                    <h3 class="metric-value text-dark fw-bold mb-0 mt-1">
                      {{ getTotalCustomers() | number }}
                      <span class="fs-7 text-muted fw-normal">Accounts</span>
                    </h3>
                  </div>
                  <div class="metric-icon-wrap bg-primary-subtle text-primary">
                    <i class="bi bi-people-fill fs-4"></i>
                  </div>
                </div>
                <div class="d-flex align-items-center justify-content-between pt-2 border-top mt-2">
                  <span class="fs-8 text-muted">
                    <i class="bi bi-person-check text-primary me-1"></i>Active: <strong>{{ getActiveCustomersCount() | number }}</strong>
                  </span>
                  @if (overviewData.contacts_summary.new_contacts_count > 0) {
                    <span class="badge bg-success-subtle text-success border border-success-subtle fs-9" title="New contacts added in this period">
                      +{{ overviewData.contacts_summary.new_contacts_count }} New
                    </span>
                  }
                </div>
              </div>
            </div>

            <!-- 2. Closed Customers -->
            <div class="col-md-4">
              <div class="metric-card app-card p-3 h-100 metric-card-purple cursor-pointer" (click)="selectTab('customer_accounts')">
                <div class="d-flex align-items-start justify-content-between mb-2">
                  <div>
                    <span class="metric-label text-muted fs-8 fw-semibold text-uppercase">Closed Customers</span>
                    <h3 class="metric-value text-dark fw-bold mb-0 mt-1">
                      {{ getClosedCustomersCount() | number }}
                      <span class="fs-7 text-muted fw-normal">Closed</span>
                    </h3>
                  </div>
                  <div class="metric-icon-wrap bg-info-subtle text-info">
                    <i class="bi bi-shield-check fs-4"></i>
                  </div>
                </div>
                <div class="d-flex align-items-center justify-content-between pt-2 border-top mt-2">
                  <span class="fs-8 text-muted">
                    <i class="bi bi-lock-fill text-muted me-1"></i>Settled & Officially Closed
                  </span>
                  <span class="badge bg-light text-secondary border fs-9">
                    Audit Logged
                  </span>
                </div>
              </div>
            </div>

            <!-- 3. Pending to Close (with severity colors & closure percentage) -->
            <div class="col-md-4">
              <div class="metric-card app-card p-3 h-100 cursor-pointer" [ngClass]="getClosureSeverityCardClass()" (click)="selectTab('customer_accounts')">
                <div class="d-flex align-items-start justify-content-between mb-1">
                  <div>
                    <span class="metric-label text-muted fs-8 fw-semibold text-uppercase">Pending to Close</span>
                    <div class="d-flex align-items-baseline gap-2 mt-1">
                      <h3 class="metric-value fw-bold mb-0" 
                        [class.text-danger]="getClosureSeverity() === 'danger'"
                        [class.text-warning]="getClosureSeverity() === 'warning'"
                        [class.text-success]="getClosureSeverity() === 'success'">
                        {{ overviewData.contacts_summary.pending_closure_count || 0 }}
                      </h3>
                      <span class="badge border fs-9" 
                        [class.bg-danger-subtle]="getClosureSeverity() === 'danger'"
                        [class.text-danger]="getClosureSeverity() === 'danger'"
                        [class.border-danger-subtle]="getClosureSeverity() === 'danger'"
                        [class.bg-warning-subtle]="getClosureSeverity() === 'warning'"
                        [class.text-warning-emphasis]="getClosureSeverity() === 'warning'"
                        [class.border-warning-subtle]="getClosureSeverity() === 'warning'"
                        [class.bg-success-subtle]="getClosureSeverity() === 'success'"
                        [class.text-success]="getClosureSeverity() === 'success'"
                        [class.border-success-subtle]="getClosureSeverity() === 'success'">
                        Closure Rate: {{ getClosurePercentage() }}%
                      </span>
                    </div>
                  </div>
                  <div class="metric-icon-wrap" 
                    [class.bg-danger-subtle]="getClosureSeverity() === 'danger'"
                    [class.text-danger]="getClosureSeverity() === 'danger'"
                    [class.bg-warning-subtle]="getClosureSeverity() === 'warning'"
                    [class.text-warning]="getClosureSeverity() === 'warning'"
                    [class.bg-success-subtle]="getClosureSeverity() === 'success'"
                    [class.text-success]="getClosureSeverity() === 'success'">
                    <i class="bi bi-hourglass-split fs-4"></i>
                  </div>
                </div>

                <!-- Severity Progress Bar & Status -->
                <div class="mt-2 pt-1">
                  <div class="d-flex align-items-center justify-content-between fs-9 mb-1">
                    <span class="fw-semibold" 
                      [class.text-danger]="getClosureSeverity() === 'danger'"
                      [class.text-warning]="getClosureSeverity() === 'warning'"
                      [class.text-success]="getClosureSeverity() === 'success'">
                      {{ getClosureSeverityStatus() }}
                    </span>
                    <span class="text-muted">Target: &gt;80%</span>
                  </div>
                  <div class="progress" style="height: 6px;">
                    <div 
                      class="progress-bar rounded-pill" 
                      role="progressbar" 
                      [class.bg-danger]="getClosureSeverity() === 'danger'"
                      [class.bg-warning]="getClosureSeverity() === 'warning'"
                      [class.bg-success]="getClosureSeverity() === 'success'"
                      [style.width.%]="getClosurePercentage()"
                      aria-valuemin="0" 
                      aria-valuemax="100">
                    </div>
                  </div>
                </div>

                <div class="d-flex align-items-center justify-content-between pt-2 border-top mt-2">
                  <span class="fs-8 text-muted">
                    {{ getClosedCustomersCount() | number }} Closed / {{ getTotalCustomers() | number }} Total
                  </span>
                  <span class="badge bg-light text-primary border fs-9 d-inline-flex align-items-center">
                    Review <i class="bi bi-arrow-right ms-1"></i>
                  </span>
                </div>
              </div>
            </div>
          </div>

          <!-- ========================================================================= -->
          <!-- 2) SECOND LINE: BRICKS SOLD & BRICKS PRODUCTION -->
          <!-- ========================================================================= -->
          <div class="row g-3 mb-3">
            <!-- 1. Bricks Sold -->
            <div class="col-md-6">
              <div class="metric-card app-card p-3 h-100 metric-card-blue">
                <div class="d-flex align-items-start justify-content-between mb-2">
                  <div>
                    <span class="metric-label text-muted fs-8 fw-semibold text-uppercase">Bricks Sold</span>
                    <h3 class="metric-value text-dark fw-bold mb-0 mt-1">
                      {{ (overviewData.sales_volume.total_bricks_sold || 0) | number }}
                      <span class="fs-7 text-muted fw-normal">pcs</span>
                    </h3>
                  </div>
                  <div class="metric-icon-wrap bg-primary-subtle text-primary">
                    <i class="bi bi-truck fs-4"></i>
                  </div>
                </div>
                <div class="d-flex align-items-center justify-content-between pt-2 border-top mt-2">
                  <span class="fs-8 text-muted">
                    <i class="bi bi-receipt me-1 text-primary"></i><strong>{{ overviewData.sales_volume.total_orders_count || 0 }}</strong> dispatches/invoices
                    @if (overviewData.sales_volume.total_orders_count > 0) {
                      <span class="text-muted ms-1">({{ getAvgBricksPerOrder() | number }} pcs/dispatch)</span>
                    }
                  </span>
                  <span class="badge bg-primary-subtle text-primary border border-primary-subtle fs-9">Sales Volume</span>
                </div>
              </div>
            </div>

            <!-- 2. Bricks Production -->
            <div class="col-md-6">
              <div class="metric-card app-card p-3 h-100 metric-card-green">
                <div class="d-flex align-items-start justify-content-between mb-2">
                  <div>
                    <span class="metric-label text-muted fs-8 fw-semibold text-uppercase">Bricks Production</span>
                    <h3 class="metric-value text-success fw-bold mb-0 mt-1">
                      {{ (overviewData.production_operations.total_bricks_produced || 0) | number }}
                      <span class="fs-7 text-muted fw-normal">pcs</span>
                    </h3>
                  </div>
                  <div class="metric-icon-wrap bg-success-subtle text-success">
                    <i class="bi bi-gear-wide-connected fs-4"></i>
                  </div>
                </div>
                <div class="d-flex align-items-center justify-content-between pt-2 border-top mt-2">
                  <span class="fs-8 text-muted">
                    <i class="bi bi-clipboard-data me-1 text-success"></i><strong>{{ overviewData.production_operations.active_worksheets_count || 0 }}</strong> active worksheets
                  </span>
                  @if (getNetStockDelta() >= 0) {
                    <span class="badge bg-success-subtle text-success border border-success-subtle fs-9" title="Plant Produced more than Dispatched">
                      +{{ getNetStockDelta() | number }} Surplus Produced
                    </span>
                  } @else {
                    <span class="badge bg-warning-subtle text-warning-emphasis border border-warning-subtle fs-9" title="Dispatched from Existing Inventory">
                      {{ getNetStockDelta() | number }} Net Inventory
                    </span>
                  }
                </div>
              </div>
            </div>
          </div>

          <!-- ========================================================================= -->
          <!-- 3) THEN: PRIORITY COLLECTIONS (TOP OVERVIEW) & PRODUCT VOLUME BREAKDOWN -->
          <!-- ========================================================================= -->
          <div class="row g-3 mb-3">
            <!-- LEFT PANEL — Priority Collections (Top Overdue Customers) -->
            <div class="col-lg-6">
              <div class="app-card h-100">
                <div class="card-header p-3 d-flex align-items-center justify-content-between flex-wrap gap-2 border-bottom">
                  <div>
                    <h6 class="card-title fw-bold mb-0">
                      <i class="bi bi-wallet2 text-danger me-2"></i>Priority Collections (Top Overdue)
                    </h6>
                    <span class="text-muted fs-8">High-value pending receivables and overdue customer ledgers</span>
                  </div>
                  <div class="d-flex align-items-center gap-2">
                    <span class="badge bg-danger text-white fs-8">
                      Total Due: {{ (overviewData.dues_summary.total_pending_due || 0) | currency:'INR' }}
                    </span>
                    <button type="button" class="btn btn-sm btn-outline-danger d-inline-flex align-items-center" (click)="selectTab('customer_accounts')">
                      <span>Audit All</span>
                      <i class="bi bi-arrow-right ms-1"></i>
                    </button>
                  </div>
                </div>
                <div class="card-body p-0">
                  @if (overviewData.dues_summary.top_overdue_customers && overviewData.dues_summary.top_overdue_customers.length > 0) {
                    <div class="table-responsive">
                      <table class="table app-table table-hover mb-0 align-middle">
                        <thead>
                          <tr>
                            <th>Customer Name</th>
                            <th>Phone</th>
                            <th class="text-end">Due Amount</th>
                            <th>Last Payment Date</th>
                            <th class="text-end">Action</th>
                          </tr>
                        </thead>
                        <tbody>
                          @for (cust of overviewData.dues_summary.top_overdue_customers; track cust.contact_id) {
                            <tr>
                              <!-- Customer Name (linked to /hrm/customer/view/{id}) -->
                              <td>
                                <a [routerLink]="['/hrm/customer/view', cust.contact_id]" class="fw-semibold text-primary text-decoration-none d-flex align-items-center">
                                  <div class="account-mini-avatar me-2 flex-shrink-0">
                                    {{ getInitials(cust.name) }}
                                  </div>
                                  <div>
                                    <span class="d-block text-truncate text-dark" style="max-width: 150px;" [title]="cust.name">
                                      {{ cust.name }}
                                    </span>
                                    <span class="fs-9 text-muted fw-normal">#{{ cust.contact_id }}</span>
                                  </div>
                                </a>
                              </td>

                              <!-- Phone -->
                              <td class="fs-8">
                                @if (cust.phone) {
                                  <a [href]="'tel:' + cust.phone" class="text-decoration-none text-muted">
                                    <i class="bi bi-telephone me-1 text-primary"></i>{{ cust.phone }}
                                  </a>
                                } @else {
                                  <span class="text-muted">-</span>
                                }
                              </td>

                              <!-- Due Amount (in danger red) -->
                              <td class="text-end fw-bold text-danger fs-7">
                                {{ cust.balance_due | currency:'INR' }}
                              </td>

                              <!-- Last Payment Date -->
                              <td class="fs-8 text-muted">
                                {{ cust.last_payment_date ? (cust.last_payment_date | date:'dd MMM yyyy') : 'No payments' }}
                              </td>

                              <!-- Action -->
                              <td class="text-end">
                                <button 
                                  type="button" 
                                  class="btn btn-xs btn-light border d-inline-flex align-items-center"
                                  (click)="auditCustomer(cust)"
                                  title="Audit customer ledger">
                                  <span>Audit</span>
                                  <i class="bi bi-chevron-right ms-1"></i>
                                </button>
                              </td>
                            </tr>
                          }
                        </tbody>
                      </table>
                    </div>
                  } @else {
                    <div class="text-center py-4 text-muted">
                      <i class="bi bi-check-circle-fill fs-2 text-success d-block mb-1"></i>
                      <p class="fs-7 fw-semibold text-dark mb-0">All Customer Accounts Clear!</p>
                      <span class="fs-8 text-muted">No overdue balances detected in this period.</span>
                    </div>
                  }
                </div>
              </div>
            </div>

            <!-- RIGHT PANEL — Product Volume Breakdown -->
            <div class="col-lg-6">
              <div class="app-card h-100">
                <div class="card-header p-3 d-flex align-items-center justify-content-between border-bottom">
                  <div>
                    <h6 class="card-title fw-bold mb-0">
                      <i class="bi bi-pie-chart text-primary me-2"></i>Product Volume Breakdown
                    </h6>
                    <span class="text-muted fs-8">Product catalogue sales distribution</span>
                  </div>
                  <span class="badge bg-light text-primary border fs-8">
                    Total Sold: {{ (overviewData.sales_volume.total_bricks_sold || 0) | number }} pcs
                  </span>
                </div>
                <div class="card-body p-0">
                  @if (overviewData.sales_volume.product_breakdown && overviewData.sales_volume.product_breakdown.length > 0) {
                    <div class="table-responsive">
                      <table class="table app-table table-hover mb-0 align-middle">
                        <thead>
                          <tr>
                            <th>Product Name</th>
                            <th class="text-end">Quantity Sold</th>
                            <th class="text-center">Unit</th>
                            <th style="width: 130px;">Share</th>
                          </tr>
                        </thead>
                        <tbody>
                          @for (prod of overviewData.sales_volume.product_breakdown; track prod.product_id) {
                            <tr>
                              <td>
                                <div class="fw-semibold text-dark">{{ prod.product_name }}</div>
                              </td>
                              <td class="text-end fw-bold text-dark fs-7">
                                {{ prod.quantity_sold | number }}
                              </td>
                              <td class="text-center">
                                <span class="badge bg-light text-secondary border fs-8">{{ prod.unit || 'pcs' }}</span>
                              </td>
                              <td>
                                <div class="d-flex align-items-center gap-2">
                                  <div class="progress flex-grow-1" style="height: 6px;">
                                    <div 
                                      class="progress-bar bg-gradient-primary rounded-pill" 
                                      role="progressbar" 
                                      [style.width.%]="getProductShare(prod.quantity_sold)"
                                      [attr.aria-valuenow]="getProductShare(prod.quantity_sold)"
                                      aria-valuemin="0" 
                                      aria-valuemax="100">
                                    </div>
                                  </div>
                                  <span class="fs-9 fw-semibold text-muted">{{ getProductShare(prod.quantity_sold) }}%</span>
                                </div>
                              </td>
                            </tr>
                          }
                        </tbody>
                      </table>
                    </div>
                  } @else {
                    <div class="text-center py-4 text-muted">
                      <i class="bi bi-box fs-2 text-muted opacity-50 d-block mb-1"></i>
                      <p class="fs-7 mb-0">No product sales recorded in this period.</p>
                    </div>
                  }
                </div>
              </div>
            </div>
          </div>

          <!-- ========================================================================= -->
          <!-- 4) DAILY DISPATCH TREND & OPERATIONAL HEALTH -->
          <!-- ========================================================================= -->
          <div class="row g-3">
            <!-- DAILY DISPATCH VOLUME & ORDERS TREND CHART -->
            <div class="col-lg-8">
              <div class="app-card h-100">
                <div class="card-header p-3 d-flex align-items-center justify-content-between flex-wrap gap-2 border-bottom">
                  <div>
                    <h6 class="card-title fw-bold mb-0">
                      <i class="bi bi-graph-up text-primary me-2"></i>Daily Dispatch & Orders Trend
                    </h6>
                    <span class="text-muted fs-8">Timeline of brick volume sold and invoice generation</span>
                  </div>
                  <span class="badge bg-light text-dark border fs-8">
                    Total Dispatched: <strong>{{ (overviewData.sales_volume.total_bricks_sold || 0) | number }} pcs</strong>
                  </span>
                </div>
                <div class="card-body p-3">
                  @if (overviewData.sales_volume.daily_volume_trend && overviewData.sales_volume.daily_volume_trend.length > 0) {
                    <div class="chart-container position-relative" style="height: 280px;">
                      <canvas 
                        baseChart
                        [data]="dailyChartData"
                        [options]="dailyChartOptions"
                        [type]="dailyChartType">
                      </canvas>
                    </div>
                  } @else {
                    <div class="text-center py-5 text-muted">
                      <i class="bi bi-bar-chart fs-1 d-block mb-2 text-muted opacity-50"></i>
                      <p class="fs-7 mb-0">No daily dispatches recorded in this selected period.</p>
                      <span class="fs-8 text-muted">Select a wider date range or check back after dispatches are logged.</span>
                    </div>
                  }
                </div>
              </div>
            </div>

            <!-- PLANT BALANCE & OPERATIONAL EFFICIENCY -->
            <div class="col-lg-4">
              <div class="app-card h-100">
                <div class="card-header p-3 border-bottom">
                  <h6 class="card-title fw-bold mb-0">
                    <i class="bi bi-activity text-success me-2"></i>Plant Operational Health
                  </h6>
                  <span class="text-muted fs-8">Production vs Dispatch Balance</span>
                </div>
                <div class="card-body p-3 d-flex flex-column justify-content-between">
                  <!-- Dispatch Ratio Meter -->
                  <div class="mb-3">
                    <div class="d-flex align-items-center justify-content-between mb-1">
                      <span class="fs-8 text-muted fw-semibold">Dispatch vs Production</span>
                      <span class="fs-8 fw-bold text-dark">{{ getProductionEfficiency() }}% Dispatched</span>
                    </div>
                    <div class="progress" style="height: 8px;">
                      <div 
                        class="progress-bar bg-success rounded-pill" 
                        role="progressbar" 
                        [style.width.%]="getProductionEfficiency()"
                        aria-valuemin="0" 
                        aria-valuemax="100">
                      </div>
                    </div>
                    <div class="d-flex align-items-center justify-content-between fs-9 text-muted mt-1">
                      <span>Produced: {{ (overviewData.production_operations.total_bricks_produced || 0) | number }}</span>
                      <span>Sold: {{ (overviewData.sales_volume.total_bricks_sold || 0) | number }}</span>
                    </div>
                  </div>

                  <!-- Quick Stats List -->
                  <ul class="list-group list-group-flush fs-8 mb-3">
                    <li class="list-group-item px-0 py-2 d-flex align-items-center justify-content-between">
                      <span class="text-muted"><i class="bi bi-layers me-2 text-primary"></i>Active Worksheets</span>
                      <span class="fw-bold text-dark">{{ overviewData.production_operations.active_worksheets_count }}</span>
                    </li>
                    <li class="list-group-item px-0 py-2 d-flex align-items-center justify-content-between">
                      <span class="text-muted"><i class="bi bi-building-add me-2 text-success"></i>Surplus Produced</span>
                      <span class="fw-bold" [class.text-success]="getNetStockDelta() >= 0" [class.text-warning]="getNetStockDelta() < 0">
                        {{ (getNetStockDelta() >= 0 ? '+' : '') + (getNetStockDelta() | number) }} Pcs
                      </span>
                    </li>
                    <li class="list-group-item px-0 py-2 d-flex align-items-center justify-content-between">
                      <span class="text-muted"><i class="bi bi-hourglass-split me-2 text-warning"></i>Pending Closure Reviews</span>
                      @if (overviewData.contacts_summary.pending_closure_count > 0) {
                        <span class="badge bg-warning text-dark">{{ overviewData.contacts_summary.pending_closure_count }} Review(s)</span>
                      } @else {
                        <span class="badge bg-light text-muted border">0 Pending</span>
                      }
                    </li>
                  </ul>

                  <!-- Navigation Shortcut -->
                  <button 
                    type="button" 
                    class="btn btn-sm btn-outline-primary w-100 d-inline-flex align-items-center justify-content-center"
                    (click)="selectTab('customer_accounts')">
                    <i class="bi bi-person-lines-fill me-1"></i> Open Customer Accounts Audit
                  </button>
                </div>
              </div>
            </div>
          </div>
        }
      </div>
    }

    <!-- ========================================================================= -->
    <!-- CUSTOMER ACCOUNTS TAB -->
    <!-- ========================================================================= -->
    @if (activeTab === 'customer_accounts') {
      <div class="tab-pane-container" id="customer-accounts-details">
        <app-accounts-audit [contactType]="'customer'" [showToolbar]="false"></app-accounts-audit>
      </div>
    }

    <!-- ========================================================================= -->
    <!-- FOLLOW-UPS & REMINDERS TAB -->
    <!-- ========================================================================= -->
    @if (activeTab === 'reminders') {
      <div class="tab-pane-container" id="reminders-hub-details">
        <app-reminders-hub [showHeaderCard]="true"></app-reminders-hub>
      </div>
    }
  </div>
</div>

